import { IActionContext } from '../helpers/ActionContext';
import { L10n } from '../L10n/L10n';
import { IErrorItem } from '../models/MultipleItemResult';
import { Set } from '../models/Set';
import { Module } from '../Module';
import { Schedules } from '../resources/Schedules';
import { IResult } from '../ServerApi/types/IResponse';
import { ISchedule } from '../ServerApi/types/ISchedule';
import { ActionHelpers } from './ActionHelpers';
import { ConfirmActionDialog } from './ConfirmActionDialog';
import { INotificationTranslationIds } from './ContentActionNotification';
import { ScheduleErrors } from './ScheduleErrors';

interface IDialogTranslationIds {
  title: string;
  body: string;
  actionName: string;
}

export class SetScheduleStatusAction {

  public static $inject = ['ScheduleErrors', 'ActionHelpers', 'ConfirmActionDialog', '$translate',  'Schedules'];

  private static EnableTranslationIds = {
    successId: 'enableSchedulesSuccess',
    errorId: 'enableSchedulesError',
    title: 'enableSchedulesDialogTitle',
    body: 'enableSchedulesDialogPrompt',
    actionName: 'enableSchedulesDialogSubmit'
  };

  private static DisableTranslationIds = {
    successId: 'disableSchedulesSuccess',
    errorId: 'disableSchedulesError',
    title: 'disableSchedulesDialogTitle',
    body: 'disableSchedulesDialogPrompt',
    actionName: 'disableSchedulesDialogSubmit'
  };

  constructor(
    private ScheduleErrors: ScheduleErrors,
    private ActionHelpers: ActionHelpers,
    private ConfirmActionDialog: ConfirmActionDialog,
    private $translate: ng.translate.ITranslateService,
    private Schedules: Schedules) { }

  public enableSchedule(schedule: ISchedule): ng.IPromise<void> {
    return this.enableSchedules(this.singleton(schedule));
  }

  public disableSchedule(schedule: ISchedule): ng.IPromise<void> {
    return this.disableSchedules(this.singleton(schedule));
  }

  private singleton(schedule: ISchedule): Set<ISchedule> {
    return Set.singleton(schedule, (s: ISchedule) => s.id);
  }

  public confirmEnableSchedules(schedules: Set<ISchedule>, context: IActionContext): ng.IPromise<IResult> {
    return this.confirmAction(
      schedules,
      context,
      () => this.enableSchedules(schedules),
      SetScheduleStatusAction.EnableTranslationIds);
  }

  public confirmDisableSchedules(schedules: Set<ISchedule>, context: IActionContext): ng.IPromise<IResult> {
    return this.confirmAction(
      schedules,
      context,
      () => this.disableSchedules(schedules),
      SetScheduleStatusAction.DisableTranslationIds);
  }

  private confirmAction(
      schedules: Set<ISchedule>,
      context: IActionContext,
      action: () => ng.IPromise<void>,
      translationIds: IDialogTranslationIds) {

    return this.ConfirmActionDialog.open({
      scope: context.scope,
      title: this.$translate.instant(translationIds.title),
      bodyText: this.$translate.instant(translationIds.body, {
        ITEM_COUNT: schedules.size(),
        NAME: L10n.elideIfLong(schedules.first().name)
      }, L10n.Interpolations.Count),
      actionName: this.$translate.instant(translationIds.actionName, {
        ITEM_COUNT: schedules.size()
      }, L10n.Interpolations.Count),
      action: action
    }).result;
  }

  private enableSchedules(schedules: Set<ISchedule>): ng.IPromise<void> {
    return this.Schedules.enable(schedules.keys())
      .then((result: IResult) =>
        this.notifyResult(schedules, result, SetScheduleStatusAction.EnableTranslationIds));
  }

  private disableSchedules(schedules: Set<ISchedule>): ng.IPromise<void> {
    return this.Schedules.disable(schedules.keys())
      .then((result: IResult) =>
        this.notifyResult(schedules, result, SetScheduleStatusAction.DisableTranslationIds));
  }

  private notifyResult(schedules: Set<ISchedule>, result: IResult, translationIds: INotificationTranslationIds) {
    this.ActionHelpers.notifyStandardError(
      schedules,
      translationIds,
      (errorItem: IErrorItem<ISchedule>) => this.ScheduleErrors.errorDetailsFor(errorItem))(result);
  }

}

Module.instance().service('SetScheduleStatusAction', SetScheduleStatusAction);
